import React, { useEffect } from 'react';
import { RouterState } from 'connected-react-router';
import { Visitor } from 'types/Visitor';
import { getNth, parseQueryParams } from 'utils/helpers';
import socket from 'utils/socket-client';
import { useVisitorKiosk } from 'contexts/VisitorKioskContext';
import Loader from 'components/Loader/Loader';
import AGMessage from 'components/AGMessage/AGMessage';
import VisitorPlate from './VisitorPlate';
import styles from './VisitorPlate.module.css';

type VisitorPlatesProps = {
    spotId: number;
    router: RouterState;
};

function VisitorPlates({ spotId, router }: VisitorPlatesProps) {
    const { minutes, calendar } = parseQueryParams(router.location.search) as { minutes?: number; calendar?: boolean };
    const {
        loading,
        errorMessage,
        successMessage,
        kioskVisitors: visitors,
        validateKioskVisitor,
        getKioskVisitors,
    } = useVisitorKiosk();

    useEffect(() => {
        getKioskVisitors(spotId);
    }, [getKioskVisitors, spotId]);

    useEffect(() => {
        socket.on('server:newVisitor', (spotPk: number) => {
            if (spotPk === Number(spotId)) {
                getKioskVisitors(spotId);
            }
        });

        return () => {
            socket.close();
        };
    }, [getKioskVisitors, spotId]);

    function handleVisitorValidation(visitorPk: number, date?: Date) {
        if (minutes) validateKioskVisitor(spotId, { pk: visitorPk, minutes });
        else if (date) {
            const now = new Date();
            const validUntil = date;
            validUntil.setHours(23, 59);

            const duration = validUntil.getTime() - now.getTime();
            const validationMinutes = Math.round(duration / 60 / 1000);

            const formattedDate =
                new Intl.DateTimeFormat('en-US', {
                    month: 'long',
                    day: '2-digit',
                }).format(date) + getNth(validUntil.getDate());

            validateKioskVisitor(spotId, { pk: visitorPk, minutes: validationMinutes, formattedDate });
        }
    }

    return (
        <>
            <div className={styles.header}>
                <h2>
                    {(visitors && visitors.length > 0) || loading
                        ? 'Tap license plate to validate'
                        : 'There are no visitors currently requesting validation'}
                </h2>
                {errorMessage && <AGMessage color="error" title={errorMessage} />}
                {successMessage && <AGMessage color="success" title={successMessage} />}
            </div>
            <div className={styles.platesContainer}>
                {!loading && visitors ? (
                    <>
                        {visitors.map((visitor: Visitor) => {
                            return (
                                <VisitorPlate
                                    key={visitor.pk}
                                    visitor={visitor}
                                    calendarMode={calendar || !minutes}
                                    validateVisitor={handleVisitorValidation}
                                />
                            );
                        })}
                    </>
                ) : (
                    <Loader />
                )}
            </div>
        </>
    );
}

export default VisitorPlates;
